import {Component, inject} from '@angular/core';
import {FormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatSelectModule} from '@angular/material/select';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {Roles} from '@core/enums/Roles';
import {Account, AdminApiService, ClientCompany, apiErrorMessage} from '@services/api/admin/admin-api.service';

export interface AccountDialogData {
  account?: Account;
  companies: ClientCompany[];
  isPlatformAdmin: boolean;
  selfId?: number;
  presetCompany?: number;
  presetRole?: number;
}

/** Ruoli assegnabili dal back office (i super admin non si creano dall'interfaccia). */
export const ASSIGNABLE_ROLES = [Roles.MANAGEMENT_SYSTEM, Roles.OPERATOR_BO, Roles.TECHNICIAN];

@Component({
  selector: 'app-account-dialog',
  standalone: true,
  imports: [ReactiveFormsModule, MatDialogModule, MatFormFieldModule, MatInputModule, MatSelectModule, TranslateModule],
  template: `
    <h2 mat-dialog-title>{{ (editing ? 'admin.edit_user' : 'admin.invite_user') | translate }}</h2>
    <form [formGroup]="form" (ngSubmit)="save()">
      <mat-dialog-content>
        @if (error) {
          <p class="dm-a-error" role="alert">{{ error }}</p>
        }
        <div class="dm-a-dialog-grid">
          <mat-form-field appearance="outline">
            <mat-label>{{ 'admin.name' | translate }}</mat-label>
            <input matInput formControlName="name" autocomplete="off"/>
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>{{ 'admin.lastname' | translate }}</mat-label>
            <input matInput formControlName="lastname" autocomplete="off"/>
          </mat-form-field>
          <mat-form-field appearance="outline" class="full">
            <mat-label>{{ 'admin.email' | translate }}</mat-label>
            <input matInput type="email" formControlName="email" autocomplete="off"/>
            @if (editing) {
              <mat-hint>{{ 'admin.email_locked' | translate }}</mat-hint>
            }
          </mat-form-field>
          <mat-form-field appearance="outline" [class.full]="!showCompany">
            <mat-label>{{ 'admin.role' | translate }}</mat-label>
            <mat-select formControlName="role">
              @for (role of roles; track role) {
                <mat-option [value]="role">{{ 'admin.role_' + role | translate }}</mat-option>
              }
            </mat-select>
            @if (roleLocked) {
              <mat-hint>{{ 'admin.role_locked' | translate }}</mat-hint>
            }
          </mat-form-field>
          @if (showCompany) {
            <mat-form-field appearance="outline">
              <mat-label>{{ 'admin.company' | translate }}</mat-label>
              <mat-select formControlName="company">
                @for (company of data.companies; track company.id) {
                  <mat-option [value]="company.id">{{ company.name }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
          }
        </div>
        @if (!editing) {
          <p class="dm-a-hint">{{ 'admin.invite_hint' | translate }}</p>
        }
      </mat-dialog-content>
      <mat-dialog-actions align="end">
        <button type="button" class="dm-a-btn ghost" mat-dialog-close>{{ 'admin.cancel' | translate }}</button>
        <button type="submit" class="dm-a-btn primary" [disabled]="form.invalid || saving">
          {{ (editing ? 'admin.save' : 'admin.send_invite') | translate }}
        </button>
      </mat-dialog-actions>
    </form>
  `,
})
export class AccountDialogComponent {
  data: AccountDialogData = inject(MAT_DIALOG_DATA);
  private dialogRef = inject(MatDialogRef<AccountDialogComponent, Account>);
  private api = inject(AdminApiService);
  private translate = inject(TranslateService);
  private fb = inject(FormBuilder);

  readonly roles = ASSIGNABLE_ROLES;
  readonly editing = !!this.data.account;
  // Il proprio ruolo e quello di un amministratore DigitalMens non si cambiano da qui
  readonly roleLocked = this.editing && (this.data.account!.id === this.data.selfId || this.data.account!.role === Roles.SUPER_ADMIN);
  readonly showCompany = this.data.isPlatformAdmin && !this.editing;
  saving = false;
  error = '';

  form = this.fb.nonNullable.group({
    name: [this.data.account?.name ?? '', Validators.required],
    lastname: [this.data.account?.lastname ?? ''],
    email: [{value: this.data.account?.email ?? '', disabled: this.editing}, [Validators.required, Validators.email]],
    role: [{value: this.data.account?.role ?? this.data.presetRole ?? Roles.TECHNICIAN, disabled: this.roleLocked}, Validators.required],
    company: [this.data.presetCompany ?? (null as number | null), this.showCompany ? Validators.required : []],
  });

  save(): void {
    if (this.form.invalid) {
      return;
    }
    this.saving = true;
    this.error = '';
    const value = this.form.getRawValue();
    const request = this.editing
      ? this.api.updateAccount(this.data.account!.id, {
          name: value.name,
          lastname: value.lastname,
          ...(this.roleLocked ? {} : {role: value.role}),
        })
      : this.api.createAccount({
          email: value.email.trim(),
          name: value.name,
          lastname: value.lastname,
          role: value.role,
          ...(this.showCompany ? {company: value.company!} : {}),
        });
    request.subscribe({
      next: (account) => this.dialogRef.close(account),
      error: (err) => {
        this.saving = false;
        this.error = apiErrorMessage(err, this.translate.instant('admin.error_generic'));
      },
    });
  }
}
